import { useCallback, useEffect, useState } from "react";
import { useLoaderData, useRevalidator } from "react-router-dom";

import { inject } from "../../../../lib/itn-ui-kit/services/service-container";
import useService from "../../../hooks/useService";

import ContactsActions from "./components/ContactsActions";
import ContactsModals from "./components/ContactsModals";
import ContactsTableSection from "./components/ContactsTableSection";
import { getMenuList } from "./config/helper";
import useContactsHandlers from "./hooks/useContactHandlers";
import { useContactStore } from "./hooks/useContactStore";
import { STRINGS } from "./utils/string";

export default function Contacts({ contactsApi, contactTypeColors = {} } = {}) {
  const currentUserService = inject("current-user-service");
  const userPermissions = currentUserService?.userRoles?.permissions || [];
  const loaderData = useLoaderData();
  const permissions = {
    ADD: userPermissions.includes("core.add_contact"),
    EDIT: userPermissions.includes("core.change_contact"),
    DELETE: userPermissions.includes("core.delete_contact"),
  };

  const contactRepository = useService(
    contactsApi?.repositoryKey || "repositories/contact-repository"
  );
  const id = contactsApi?.ownerId ?? loaderData?.id;
  const contacts = contactsApi?.contacts ?? loaderData?.contacts;
  const fieldOptions = contactsApi?.fieldOptions ?? loaderData?.fieldOptions ?? [];
  const nextPage = contactsApi?.nextPage ?? loaderData?.nextPage;
  const nextLimit = contactsApi?.nextLimit ?? loaderData?.nextLimit;
  const initialShowMore = contactsApi?.initialShowMore ?? loaderData?.initialShowMore;
  const [visibleData, setVisibleData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showMore, setShowMore] = useState(initialShowMore);
  const search = useContactStore((state) => state.search);
  const { revalidate } = useRevalidator();

  const handlers = useContactsHandlers({
    id,
    contactRepository,
    revalidate,
  });

  const menuList = getMenuList(fieldOptions, handlers.openAddModal);

  const queryContacts = useCallback(async (queryParams) => {
    if (typeof contactsApi?.query === "function") {
      return await contactsApi.query(contactRepository, id, queryParams);
    }
    return await contactRepository.query(id, queryParams);
  }, [contactRepository, contactsApi, id]);

  useEffect(() => {
    setVisibleData(contacts || []);
    setShowMore(!!nextPage);
  }, [contacts, nextPage]);

  useEffect(() => {
    (async () => {
      try {
        const queryParams = {
          limit: nextLimit ?? STRINGS.DEFAULT_PAGINATION_LIMIT,
          offset: 0,
          ...(search.trim() && { search }),
        };
        const items = await queryContacts(queryParams);
        const nextPageUrl = await contactRepository.getNextPage();
        setVisibleData(items || []);
        setShowMore(!!nextPageUrl);
      } catch (error) {
        handlers.handleFailure(error);
      }
    })();
  }, [search, nextLimit, queryContacts]);

  const loadMore = async () => {
    if (isLoading || !showMore) {
      return;
    }
    try {
      setIsLoading(true);
      const offset = visibleData.length;
      const limit = nextLimit ?? STRINGS.DEFAULT_PAGINATION_LIMIT;
      const items = await queryContacts({
        limit,
        offset,
        search,
      });
      const nextPageUrl = await contactRepository.getNextPage();
      setVisibleData((prev) => [...prev, ...items]);
      setShowMore(!!nextPageUrl);
    } catch (error) {
      handlers.handleFailure(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-6 px-4 py-2">
      <ContactsActions permissions={permissions} menuList={menuList} />
      <ContactsTableSection
        visibleData={visibleData}
        fieldOptions={fieldOptions}
        contactTypeColors={contactTypeColors}
        permissions={permissions}
        editHandler={handlers.openEditModal}
        deleteHandler={handlers.openDeleteModal}
        loading={isLoading}
        showMore={showMore}
        loadMore={loadMore}
      />
      <ContactsModals
        mode={handlers.mode}
        modalSize={handlers.modalSize}
        additionalDetails={handlers.additionalDetails}
        openModal={handlers.openModal}
        handleCloseModal={handlers.handleCloseModal}
        showFailureModal={handlers.showFailureModal}
        handleFailure={handlers.handleFailure}
      />
    </div>
  );
}
